import { type TAllowedMime, allowedMime } from "@api";
import type { PresignedPost } from "@aws-sdk/s3-presigned-post";
import type { IDocument, TDocumentMetadataTypes } from "@bbj-types/document";
import { getDefaultDocumentMetadata } from "@bbj-types/document/constant";
import type { DocumentTypes } from "@bbj-types/document/enum";
import type { ObjectId } from "@bbj-types/utils";
import { useCallback } from "react";
import { axios, axiosInstance } from "./axios";

export interface IUploadDocument extends PresignedPost {
  document: IDocument;
}

export interface IUploadDocumentBody<T extends DocumentTypes = DocumentTypes> {
  name: string;
  typeDoc: T;
  mimeType: TAllowedMime;
  isDeletable: boolean;
  organisation?: ObjectId;
  metadata?: T extends keyof TDocumentMetadataTypes ? TDocumentMetadataTypes[T] : undefined;
}

export const uploadDocument = <T extends DocumentTypes = DocumentTypes>(
  name: string,
  typeDoc: T,
  mimeType: TAllowedMime,
  isDeletable: boolean,
  organisation?: ObjectId,
  metadata?: T extends keyof TDocumentMetadataTypes ? TDocumentMetadataTypes[T] : undefined
) => {
  let defaultMetadata = metadata;
  if (!defaultMetadata) {
    defaultMetadata = getDefaultDocumentMetadata(typeDoc);
  }
  return axiosInstance.post<IUploadDocument>(
    "/upload",
    {
      name,
      typeDoc,
      mimeType,
      organisation,
      metadata: defaultMetadata,
      isDeletable,
    },
    {
      withCredentials: true,
    }
  );
};

export const useUploadDocument = () => {
  const [state, execute, cancel] = axios<IUploadDocument, IUploadDocumentBody, unknown>(
    {
      url: "/upload",
      method: "POST",
      withCredentials: true,
    },
    { manual: true }
  );

  const callback = useCallback(
    <T extends DocumentTypes = DocumentTypes>(
      name: string,
      typeDoc: T,
      mimeType: TAllowedMime,
      isDeletable: boolean,
      organisation?: ObjectId,
      metadata?: T extends keyof TDocumentMetadataTypes ? TDocumentMetadataTypes[T] : undefined
    ) => {
      let defaultMetadata = metadata;
      if (!allowedMime.includes(mimeType)) {
        throw new Error("Invalid MIME Type");
      }
      if (!defaultMetadata) {
        defaultMetadata = getDefaultDocumentMetadata(typeDoc);
      }
      return execute({
        data: {
          name,
          typeDoc,
          mimeType,
          organisation,
          metadata: defaultMetadata,
          isDeletable,
        },
      });
    },
    [execute]
  );

  return [state, callback, cancel] as const;
};
